import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { TrackedChange } from 'models/changes'

const ADDED = 'ADDED'
const DELETED = 'DELETED'

const normalize = (changeType?: string | null): string =>
    (changeType ?? '').toUpperCase()

/** Matches an element to its track change by substring — server's
 *  `contentItemElementId` is a compound id (e.g. `"parent_element"`) that
 *  contains the element id. Same convention as `getTrackChangesDataById`. */
const findTrackChangeForElement = (
    elementId: string | undefined,
    trackChanges: TrackedChange[]
): TrackedChange | undefined => {
    if (!elementId) return undefined
    return trackChanges.find((tc) =>
        tc.contentItemElementId?.includes(elementId)
    )
}

/**
 * Predicts the paragraph's `elements` shape AFTER the user clicks accept or
 * reject on the given track changes. Used by the duplicate-key pre-check so
 * we can extract the paragraph's resulting key BEFORE the mutation fires
 * and block it if that key would collide with a sibling.
 *
 * Per-element transform applied:
 *   Accept ADDED   → element kept, changeType cleared (becomes base content)
 *   Accept DELETED → element removed (the deletion takes effect)
 *   Reject ADDED   → element removed (the addition is undone)
 *   Reject DELETED → element kept, changeType cleared (the deletion is undone)
 *
 * Other change types pass through unchanged. Element-to-track-change matching
 * uses substring on `contentItemElementId` (see `findTrackChangeForElement`).
 */
export const simulatePostActionElements = (
    elements: Partial<TextContentElement>[],
    trackChanges: TrackedChange[],
    isAccept: boolean
): Partial<TextContentElement>[] => {
    const tcs = trackChanges ?? []

    return (elements ?? []).flatMap((el) => {
        const tc = findTrackChangeForElement(el.id, tcs)
        if (!tc) return [el]

        const ct = normalize(tc.changeType)
        if (ct === ADDED) {
            return isAccept ? [{ ...el, changeType: null }] : []
        }
        if (ct === DELETED) {
            return isAccept ? [] : [{ ...el, changeType: null }]
        }
        return [el]
    })
}

/** Group track changes by their owning paragraph (`contentItemId`). */
export const groupTrackChangesByParagraph = (
    trackChanges: TrackedChange[]
): Map<string, TrackedChange[]> => {
    const map = new Map<string, TrackedChange[]>()
    trackChanges.forEach((tc) => {
        if (!tc.contentItemId) return
        const existing = map.get(tc.contentItemId)
        if (existing) {
            existing.push(tc)
        } else {
            map.set(tc.contentItemId, [tc])
        }
    })
    return map
}
